import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, Grid, IconButton, Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertLineReportDTO } from "../../../models/alert-line-report/alert-line-report-dto";
import { UpdateAlertLineReportStatusFilter } from "../../../models/alert-line-report/update-alert-line-report-status-filter";
import { AlertLineReportStatusDTO } from "../../../models/alert-line-report-status/alert-line-report-status-dto";
import { AlertLineReportStatusRef } from "../../../models/alert-line-report-status/alert-line-report-status-ref";
import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { AlertLineReportStatusApiService } from "../../../services/alert-line/alert-line-report-status-api.service";
import { UpdateAlertLineReportStatusApiService } from "../../../services/alert-line/update-alert-line-report-status-api.service";
import ConfirmationChangesDialog from "../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../DialogActions/DialogActions";
import DialogTitle from "../../DialogTitle/DialogTitle";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";

interface Props {
  alertLineReport: AlertLineReportDTO;
  handleUpdateParent?: () => void;
}

interface State {
  open: boolean;
  loading: boolean;
  newStatus: AlertLineReportStatusRef | null;
  error: string;
  CCDOpen: boolean;
}

const UpdateAlertLineReportStatus = ({ alertLineReport, handleUpdateParent }: Props) => {
  const tRP = "components.update_alert_line_report_status";
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    open: false,
    loading: false,
    newStatus: alertLineReport.alertLineReportStatus,
    error: "",
    CCDOpen: false,
  });

  const handleChangeStatus = useCallback((value: AlertLineReportStatusDTO) => {
    setState((state) => ({
      ...state,
      newStatus: value,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    if (state.newStatus && state.newStatus.id !== alertLineReport.alertLineReportStatus?.id) {
      try {
        const response = await new UpdateAlertLineReportStatusApiService().loadItems({
          alertLineReportID: alertLineReport.id,
          alertLineReportStatusId: state.newStatus.id,
        } as UpdateAlertLineReportStatusFilter);
        if (!response.error) {
          setState((state) => ({ ...state, open: false }));
          handleUpdateParent && handleUpdateParent();
        } else {
          setState((state) => ({ ...state, error: response.error }));
        }
      } catch (error) {
        setState((state) => ({ ...state, error }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    }
  }, [alertLineReport.alertLineReportStatus?.id, alertLineReport.id, handleUpdateParent, state.newStatus]);

  return (
    <>
      <IconButton onClick={() => setState((state) => ({ ...state, open: true }))}>
        <FontAwesomeIcon icon={faEllipsisV} />
      </IconButton>
      <Dialog open={state.open}>
        <DialogTitle
          handleClose={() =>
            setState((state) => ({
              ...state,
              ...(!state.newStatus ? { CCDOpen: true } : { open: false }),
            }))
          }
          title={`${t(`${tRP}.title`)}`}
        />
        <Grid>
          <LoadingOverlay
            error={state.error}
            loading={state.loading}
          >
            <DialogContent>
              <Typography>{`${t(`${tRP}.update_report_status_to`)}`} : </Typography>
              <AutocompleteDropdown
                serviceApi={new AlertLineReportStatusApiService()}
                field={"alertLineReportStatus"}
                labelKey={`${t(`${tRP}.status`)}`}
                handleChange={(field, value) => handleChangeStatus(value)}
                sxProps={{ paddingTop: "10px" }}
                selectedValue={state.newStatus}
              />
            </DialogContent>
            <DialogActions
              handleSave={handleSave}
              handleCancel={() =>
                setState((state) => ({
                  ...state,
                  ...(!state.newStatus ? { CCDOpen: true } : { open: false }),
                }))
              }
              disabledSaveButton={!state.newStatus || state.newStatus?.id === alertLineReport.alertLineReportStatus?.id}
            />
          </LoadingOverlay>
        </Grid>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({
            open: false,
            loading: false,
            newStatus: alertLineReport.alertLineReportStatus,
            error: "",
            CCDOpen: false,
          }))
        }
      />
    </>
  );
};
export default UpdateAlertLineReportStatus;
